/**
 * Widget V2 - Auth State Context
 *
 * Manages user authentication state.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useMemo, useRef } from "react";
import api, { getToken, clearTokens, getActiveClientId, setActiveClientId, TOKEN_KEY } from "/imports/api/client";
import { unbindDevice } from "/imports/libs/push";

// The widget slug is the first path segment (routes are /:slug/*). Slug-less
// routes (email verify, checkout landings, legal pages) have no club to scope
// against, so they resolve the session by token alone as before.
const SLUGLESS_PREFIXES = ["auth", "checkout", "legal"];
function currentWidgetSlug() {
  const seg = window.location.pathname.split("/").filter(Boolean)[0];
  if (!seg || SLUGLESS_PREFIXES.includes(seg)) return null;
  return seg;
}

/**
 * Initial state
 */
const initialState = {
  user: null,
  client: null,
  role: null, // "client" | "trainer" | null
  family: [],
  activeClientId: null,
  isAuthenticated: false,
  loading: true, // Start loading to check existing token
  error: null
};

// Pick the client the user should be acting as: the persisted selection when it
// still belongs to the family, otherwise the head (self). Returns null when the
// head is the choice so the persisted value stays cleared and the API defaults
// to the head.
function reconcileActiveClientId(family, fallbackId) {
  const persisted = getActiveClientId();
  if (persisted && family.some((f) => f._id === persisted)) return persisted;
  setActiveClientId(null);
  const self = family.find((f) => f.isSelf);
  return self?._id || fallbackId || null;
}

/**
 * Action types
 */
const ActionTypes = {
  AUTH_START: "AUTH_START",
  AUTH_SUCCESS: "AUTH_SUCCESS",
  AUTH_ERROR: "AUTH_ERROR",
  LOGOUT: "LOGOUT",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_LOADING: "SET_LOADING",
  SET_FAMILY: "SET_FAMILY",
  SWITCH_CLIENT: "SWITCH_CLIENT"
};

/**
 * Reducer
 */
function authReducer(state, action) {
  switch (action.type) {
    case ActionTypes.AUTH_START:
      return {
        ...state,
        loading: true,
        error: null
      };

    case ActionTypes.AUTH_SUCCESS: {
      const family = action.payload.family || [];
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        client: action.payload.client || null,
        role: action.payload.role || null,
        family,
        activeClientId: reconcileActiveClientId(family, action.payload.client?._id),
        error: null
      };
    }

    case ActionTypes.SET_FAMILY: {
      const family = action.payload || [];
      return {
        ...state,
        family,
        activeClientId: reconcileActiveClientId(family, state.client?._id)
      };
    }

    case ActionTypes.SWITCH_CLIENT:
      return {
        ...state,
        activeClientId: action.payload
      };

    case ActionTypes.AUTH_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case ActionTypes.LOGOUT:
      return {
        ...initialState,
        loading: false
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    default:
      return state;
  }
}

/**
 * Context
 */
const AuthContext = createContext(null);

/**
 * Provider component
 */
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  /**
   * Check for existing session on mount
   */
  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (!token) {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        return;
      }

      try {
        // One retry covers a phone waking up before its network does, which
        // otherwise read as a dead session.
        const data = await api.getCurrentUser(currentWidgetSlug()).catch((err) => {
          if (err.status) throw err;
          return new Promise((resolve) => setTimeout(resolve, 1500)).then(() => api.getCurrentUser(currentWidgetSlug()));
        });
        dispatch({
          type: ActionTypes.AUTH_SUCCESS,
          payload: {
            user: data.user,
            client: data.user?.client,
            role: data.role,
            family: data.user?.family
          }
        });
      } catch (err) {
        // A club mismatch means the token belongs to a different club, not that
        // it's invalid — leave it in place so that club's session survives, and
        // just present as signed-out here so they log in or claim at this club.
        // Only a rejected session (401, or the account is gone) is forgotten; a
        // network error or a 5xx during a deploy keeps the tokens for next time.
        if (err.code !== "CLUB_MISMATCH" && (err.status === 401 || err.status === 404)) clearTokens();
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    };

    checkAuth();
  }, []);

  /**
   * Login with email/password
   */
  const login = useCallback(async (email, password, saasId = null) => {
    dispatch({ type: ActionTypes.AUTH_START });

    try {
      const data = await api.login(email, password, saasId);

      dispatch({
        type: ActionTypes.AUTH_SUCCESS,
        payload: {
          user: data.user,
          client: data.user?.client,
          role: data.role
        }
      });

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.AUTH_ERROR,
        payload: err.message || "Login failed"
      });
      throw err;
    }
  }, []);

  /**
   * Register new user
   */
  const register = useCallback(async (userData) => {
    dispatch({ type: ActionTypes.AUTH_START });

    try {
      const data = await api.register(userData);

      // No token means the account needs email verification before it can be
      // used — don't mark the session authenticated.
      if (data?.token) {
        dispatch({
          type: ActionTypes.AUTH_SUCCESS,
          payload: {
            user: data.user,
            client: data.user?.client
          }
        });
      } else {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.AUTH_ERROR,
        payload: err.message || "Registration failed"
      });
      throw err;
    }
  }, []);

  /**
   * Sign in or sign up with Google. Only "signed_in" and "needs_onboard" carry a
   * session; "needs_profile" still has to collect a phone number, so the caller
   * routes on `status` rather than assuming an authenticated user.
   */
  const googleSignIn = useCallback(async (code, slug) => {
    dispatch({ type: ActionTypes.AUTH_START });

    try {
      const data = await api.googleAuth(code, slug);

      if (data?.token) {
        dispatch({
          type: ActionTypes.AUTH_SUCCESS,
          payload: {
            user: data.user,
            client: data.user?.client,
            role: data.role
          }
        });
      } else {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.AUTH_ERROR,
        payload: err.message || "Google sign-in failed"
      });
      throw err;
    }
  }, []);

  /**
   * Finish a first-time Google signup once the phone number has been collected
   */
  const googleCompleteProfile = useCallback(async (profileToken, phone) => {
    dispatch({ type: ActionTypes.AUTH_START });

    try {
      const data = await api.googleComplete(profileToken, phone);

      dispatch({
        type: ActionTypes.AUTH_SUCCESS,
        payload: {
          user: data.user,
          client: data.user?.client,
          role: data.role
        }
      });

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.AUTH_ERROR,
        payload: err.message || "Could not complete signup"
      });
      throw err;
    }
  }, []);

  /**
   * Claim an existing client record using a club-issued access code
   */
  const claim = useCallback(async (claimData) => {
    dispatch({ type: ActionTypes.AUTH_START });

    try {
      const data = await api.claim(claimData);

      if (data?.token) {
        dispatch({
          type: ActionTypes.AUTH_SUCCESS,
          payload: {
            user: data.user,
            client: data.user?.client
          }
        });
      } else {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.AUTH_ERROR,
        payload: err.message || "Could not claim your account"
      });
      throw err;
    }
  }, []);

  /**
   * Claim a trainer account using a club-issued access code. Sets the password
   * and logs the trainer straight in (no email verification).
   */
  const trainerClaim = useCallback(async (claimData) => {
    dispatch({ type: ActionTypes.AUTH_START });

    try {
      const data = await api.trainerClaim(claimData);

      if (data?.token) {
        dispatch({
          type: ActionTypes.AUTH_SUCCESS,
          payload: {
            user: data.user,
            role: data.role
          }
        });
      } else {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.AUTH_ERROR,
        payload: err.message || "Could not claim your trainer account"
      });
      throw err;
    }
  }, []);

  /**
   * Complete an email-based claim from a link token, which logs the user in
   */
  const completeEmailClaim = useCallback(async (token, password, phoneNumber) => {
    dispatch({ type: ActionTypes.AUTH_START });

    try {
      const data = await api.claimEmail(token, password, phoneNumber);

      dispatch({
        type: ActionTypes.AUTH_SUCCESS,
        payload: {
          user: data.user,
          client: data.user?.client
        }
      });

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.AUTH_ERROR,
        payload: err.message || "Could not complete signup"
      });
      throw err;
    }
  }, []);

  /**
   * Request a password-reset link. Stateless — resolves regardless of whether
   * the email is registered, so the UI can show a generic confirmation.
   */
  const forgotPassword = useCallback(async (email, slug) => {
    return api.forgotPassword(email, slug);
  }, []);

  /**
   * Set a new password from a reset-link token. Does not sign the user in —
   * they re-authenticate with the new password on the sign-in page.
   */
  const resetPassword = useCallback(async (token, password) => {
    return api.resetPassword(token, password);
  }, []);

  /**
   * Complete email verification from a link token, which logs the user in
   */
  const completeVerification = useCallback(async (token) => {
    dispatch({ type: ActionTypes.AUTH_START });

    try {
      const data = await api.verifyEmail(token);

      dispatch({
        type: ActionTypes.AUTH_SUCCESS,
        payload: {
          user: data.user,
          client: data.user?.client
        }
      });

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.AUTH_ERROR,
        payload: err.message || "Verification failed"
      });
      throw err;
    }
  }, []);

  /**
   * Logout. Takes this phone's notifications off the account first — the request
   * needs the token that is about to be cleared — so whoever signs in next on a
   * shared phone never gets the previous person's. Bounded and best-effort.
   */
  const logout = useCallback(async () => {
    const slug = currentWidgetSlug();
    if (slug) await unbindDevice(slug).catch((err) => console.warn("Push unbind on logout failed:", err?.message));
    api.logout();
    dispatch({ type: ActionTypes.LOGOUT });
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    if (!getToken()) return;

    try {
      const data = await api.getCurrentUser(currentWidgetSlug());
      dispatch({
        type: ActionTypes.AUTH_SUCCESS,
        payload: {
          user: data.user,
          client: data.user?.client,
          role: data.role,
          family: data.user?.family
        }
      });
    } catch (err) {
      console.error("Failed to refresh user:", err);
    }
  }, []);

  // Mirror the latest auth flag into a ref so the storage listener (attached
  // once) can read current state without re-subscribing on every change.
  const isAuthedRef = useRef(state.isAuthenticated);
  useEffect(() => {
    isAuthedRef.current = state.isAuthenticated;
  }, [state.isAuthenticated]);

  // Keep auth state in sync across the user's open tabs. The storage event fires
  // only in the *other* tabs when localStorage changes. We act only on a real
  // login/logout transition (ignoring token rotation, which would otherwise
  // loop). Logout is a synchronous reset. Login does a full reload so the
  // mount-time auth check rebuilds the session — an in-place re-fetch races the
  // just-written token and intermittently left this tab logged out.
  useEffect(() => {
    const syncAuthAcrossTabs = (event) => {
      if (event.key !== null && event.key !== TOKEN_KEY) return;

      const hasToken = !!getToken();
      if (hasToken === isAuthedRef.current) return;

      if (hasToken) window.location.reload();
      else dispatch({ type: ActionTypes.LOGOUT });
    };

    window.addEventListener("storage", syncAuthAcrossTabs);
    return () => window.removeEventListener("storage", syncAuthAcrossTabs);
  }, []);

  /**
   * Load the family the user can act as (self + dependents). Called by the
   * layout once authenticated so the account menu can offer profile switching.
   */
  const loadFamily = useCallback(async (slug) => {
    if (!slug || !getToken()) return;

    try {
      const data = await api.getFamily(slug);
      dispatch({ type: ActionTypes.SET_FAMILY, payload: data.family || [] });
    } catch (err) {
      console.error("Failed to load family:", err);
    }
  }, []);

  /**
   * Switch the active profile. Persists the selection per-device; selecting the
   * head clears the persisted value so the API defaults back to the head.
   */
  const switchClient = useCallback(
    (clientId) => {
      const self = state.family.find((f) => f.isSelf);
      const isSelf = !clientId || clientId === self?._id;
      setActiveClientId(isSelf ? null : clientId);
      dispatch({ type: ActionTypes.SWITCH_CLIENT, payload: isSelf ? self?._id || null : clientId });
    },
    [state.family]
  );

  const activeClient = useMemo(
    () => state.family.find((f) => f._id === state.activeClientId) || state.family.find((f) => f.isSelf) || null,
    [state.family, state.activeClientId]
  );

  const value = useMemo(
    () => ({
      ...state,
      activeClient,
      login,
      register,
      googleSignIn,
      googleCompleteProfile,
      claim,
      trainerClaim,
      completeEmailClaim,
      completeVerification,
      forgotPassword,
      resetPassword,
      logout,
      clearError,
      refreshUser,
      loadFamily,
      switchClient
    }),
    [
      state,
      activeClient,
      login,
      register,
      googleSignIn,
      googleCompleteProfile,
      claim,
      trainerClaim,
      completeEmailClaim,
      completeVerification,
      forgotPassword,
      resetPassword,
      logout,
      clearError,
      refreshUser,
      loadFamily,
      switchClient
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
